import React, { useEffect } from 'react';
import Pager from '../pager';
import Helmet from '../helmet';
import PropTypes from 'prop-types';
import SearchHeader from '../search-header';
import PropertyCard from '../property-card';
import { omitFromObject } from 'utils/object';
import StickyFilterBar from '../sticky-filter-bar';
import { segment, delighted } from '@leadhome/utils';
import FeaturedProvinces from '../featured-provinces';
import NoPropertyResults from '../no-property-results';
import PropertyTypeCounts from '../property-type-counts';
import NoStrictMatchResults from 'components/no-strict-match-results';
import formatLocationForSearch from 'utils/format-location-for-search';
import SearchPageBreadCrumbs from '../breadcrumbs/search-page-breadcrumbs';
import { defaultPropertyTypeInfo, propertyTypeMap } from 'utils/property';
import {
  PropertyContainer,
  PropertyCardWrapper,
  PagerWrapper
} from 'styles/search';
import DefaultLayout from '@oxygen/ab/DefaultLayout';

const PropertyForSale = ({
  title,
  description,
  properties,
  provinces,
  locations = [],
  page,
  propertyType,
  minBeds,
  minBaths,
  minParking,
  minPrice,
  maxPrice,
  minStudies,
  parkingType,
  isPetFriendly,
  internetType,
  isFixerUpper,
  hasPrivatePool,
  hasFlatOrDomesticQuarters,
  hasGarden,
  sharedFeatures,
  sort,
  propertyTypeCounts,
  isHomePage,
  sendListingAlerts
}) => {
  const listings = properties?.items;
  const propertyTypesAsArray = Array.isArray(propertyType)
    ? propertyType
    : [propertyType];

  const defaultSearch = {
    propertyType: propertyTypesAsArray,
    location: locations.map(formatLocationForSearch),
    minBeds,
    minBaths,
    minParking,
    minPrice,
    maxPrice,
    minStudies,
    parkingType,
    isPetFriendly,
    internetType,
    isFixerUpper,
    hasPrivatePool,
    hasFlatOrDomesticQuarters,
    hasGarden,
    sharedFeatures,
    sort
  };

  const noStrictMatches =
    page === 1 && listings.length > 0 && !listings[0].isStrictMatch;

  useEffect(() => {
    delighted.survey();
  }, []);

  useEffect(() => {
    if (!window?.analytics) {
      return;
    }

    const isEmptyQuery =
      !defaultSearch.minBeds &&
      !defaultSearch.minBaths &&
      !defaultSearch.minParking &&
      !defaultSearch.minPrice &&
      !defaultSearch.maxPrice &&
      !defaultSearch.minStudies &&
      !defaultSearch.parkingType &&
      !defaultSearch.isPetFriendly &&
      !defaultSearch.internetType &&
      !defaultSearch.isFixerUpper &&
      !defaultSearch.hasPrivatePool &&
      !defaultSearch.hasFlatOrDomesticQuarters &&
      !defaultSearch.hasGarden &&
      locations.length === 0 &&
      propertyTypesAsArray.length === 0 &&
      sharedFeatures.length === 0;

    if (isEmptyQuery) {
      return;
    }

    const query = omitFromObject(defaultSearch, ['location', 'sort']);

    query.suburbs = locations
      .filter((location) => location.type === 'suburb')
      .map((suburb) => suburb.suburbId);

    query.provinces = locations
      .filter((location) => location.type === 'province')
      .map((province) => province.provinceId);

    query.towns = locations
      .filter((location) => location.type === 'town')
      .map((town) => town.townId);
    if (listings.length > 0) {
      segment.track('PropertySearch: SearchYieldedResults', query);
    } else {
      segment.track('PropertySearch: NoSearchResults', query);
    }
    const segmentPayload = {
      ...query,
      page,
      pageSize: properties?.pageSize,
      resultsCount: properties?.total
    };

    segment.track('Property Search Results', segmentPayload);
  }, [
    defaultSearch,
    listings.length,
    propertyTypesAsArray,
    locations,
    sharedFeatures.length,
    page,
    properties.pageSize,
    properties.total
  ]);

  const propertyTypeInfo =
    propertyTypeMap[propertyType] || defaultPropertyTypeInfo;

  return (
    <DefaultLayout fluid>
      <Helmet title={title} description={description} />
      <StickyFilterBar
        search={defaultSearch}
        sendListingAlerts={sendListingAlerts}
      />
      {!isHomePage && (
        <SearchPageBreadCrumbs
          locations={locations}
          propertyTypes={propertyTypesAsArray}
        />
      )}
      {listings.length > 0 ? (
        <>
          {!isHomePage && (
            <SearchHeader
              mainHeading={propertyTypeInfo.mainHeading}
              locationNames={locations.map(getLocationName)}
            />
          )}
          {isHomePage && (
            <FeaturedProvinces
              locationLinkPrefix={propertyTypeInfo.locationLinkPrefix}
              provinces={provinces}
            />
          )}
          {noStrictMatches && <NoStrictMatchResults />}
          <PropertyContainer>
            {listings?.map((listing, index) => (
              <PropertyCardWrapper key={index}>
                <PropertyCard {...listing} />
              </PropertyCardWrapper>
            ))}
          </PropertyContainer>
          <PagerWrapper>
            <Pager
              currentPage={Math.abs(properties.page) || 1}
              totalPages={properties.totalPages}
            />
          </PagerWrapper>
          {propertyTypeCounts && locations.length === 1 && (
            <PropertyTypeCounts
              propertyTypeCounts={propertyTypeCounts}
              location={locations[0]}
            />
          )}
        </>
      ) : (
        <NoPropertyResults />
      )}
    </DefaultLayout>
  );
};

function getLocationName(location) {
  return location.suburb || location.town || location.province;
}

PropertyForSale.propTypes = {
  provinces: PropTypes.array,
  locations: PropTypes.array,
  minBeds: PropTypes.number,
  minBaths: PropTypes.number,
  minParking: PropTypes.number,
  minPrice: PropTypes.number,
  maxPrice: PropTypes.number,
  parkingType: PropTypes.number,
  parking: PropTypes.number,
  isPetFriendly: PropTypes.bool,
  internetType: PropTypes.number,
  isFixerUpper: PropTypes.bool,
  hasPrivatePool: PropTypes.bool,
  hasFlatOrDomesticQuarters: PropTypes.bool,
  hasGarden: PropTypes.bool,
  sharedFeatures: PropTypes.array,
  sort: PropTypes.oneOf(['-price', 'price', 'date']),
  propertyType: PropTypes.oneOfType([
    PropTypes.oneOf(['house', 'apartment', 'townhouse']),
    PropTypes.arrayOf(PropTypes.oneOf(['house', 'apartment', 'townhouse']))
  ]),
  isHomePage: PropTypes.bool,
  title: PropTypes.string,
  description: PropTypes.string,
  propertyTypeCounts: PropTypes.object,
  properties: PropTypes.shape({
    page: PropTypes.number,
    pageSize: PropTypes.number,
    total: PropTypes.number,
    totalPages: PropTypes.number,
    items: PropTypes.array
  }),
  sendListingAlerts: PropTypes.bool
};

export default React.memo(PropertyForSale);
